import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, ArrowLeft, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Booking.css';

const Booking = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBooked, setIsBooked] = useState(false);

  // Available time slots (30-minute intake sessions)
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30'
  ];

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === month;
      const isToday = date.toDateString() === currentDate.toDateString();
      const isPast = date < currentDate;
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      
      days.push({
        date,
        isCurrentMonth,
        isToday,
        isPast,
        isWeekend,
        isAvailable: !isPast && !isWeekend && isCurrentMonth
      });
    }
    
    return days;
  };

  // Handle date selection
  const handleDateSelect = (day) => {
    if (day.isAvailable) {
      setSelectedDate(day.date);
      setSelectedTime(null);
      // In a real implementation, you would fetch available slots from Google Calendar API
      setAvailableSlots(timeSlots.filter(slot => Math.random() > 0.3)); // Simulate available slots
    }
  };

  // Handle time selection
  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Rate limiting for booking submissions
  const [lastBookingAttempt, setLastBookingAttempt] = useState(0);
  const BOOKING_COOLDOWN = 5000; // 5 seconds between booking attempts

  // Handle booking submission
  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) return;
    
    // Rate limiting check
    const now = Date.now();
    if (now - lastBookingAttempt < BOOKING_COOLDOWN) {
      alert('Je kunt niet zo snel achter elkaar boeken. Probeer het over een paar seconden opnieuw.');
      return;
    }
    
    setLastBookingAttempt(now);
    setIsLoading(true);
    
    try {
      // In a real implementation, you would:
      // 1. Call Google Calendar API to create the event
      // 2. Send confirmation email
      // 3. Update availability
      // 4. Verify the time slot is still available
      
      // Simulate API call with additional delay for bot protection
      await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 1000));
      
      setIsBooked(true);
    } catch (error) {
      console.error('Booking failed:', error);
      alert('Er is een fout opgetreden bij het boeken. Probeer het later opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
  ];

  if (isBooked) {
    return (
      <div className="booking-page">
        <div className="container">
          <motion.div 
            className="booking-success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle size={64} className="success-icon" />
            <h1>Afspraak Bevestigd!</h1>
            <p>Je intakegesprek is succesvol ingepland voor:</p>
            <div className="booking-details">
              <p><strong>Datum:</strong> {selectedDate?.toLocaleDateString('nl-NL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
              <p><strong>Tijd:</strong> {selectedTime}</p>
              <p><strong>Duur:</strong> 30 minuten</p>
            </div>
            <p>Je ontvangt binnenkort een bevestiging per e-mail met verdere details.</p>
            <div className="booking-actions">
              <Link to="/" className="btn btn-primary">Terug naar Home</Link>
              <Link to="/contact" className="btn btn-secondary">Contact</Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <div className="container">
        {/* Header */}
        <motion.div 
          className="booking-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/diensten" className="back-link">
            <ArrowLeft size={20} />
            Terug naar Diensten
          </Link>
          <h1>Intakegesprek Boeken</h1>
          <p>Selecteer een datum en tijd voor je gratis kennismakingsgesprek van 30 minuten.</p>
        </motion.div>

        <div className="booking-content">
          {/* Calendar Section */}
          <motion.div 
            className="calendar-section"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="calendar-header">
              <button onClick={goToPreviousMonth} className="month-nav-btn">
                ←
              </button>
              <h2>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h2>
              <button onClick={goToNextMonth} className="month-nav-btn">
                →
              </button>
            </div>

            <div className="calendar-grid">
              <div className="calendar-weekdays">
                {['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'].map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>
              
              <div className="calendar-days">
                {calendarDays.map((day, index) => (
                  <button
                    key={index}
                    className={`calendar-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${
                      day.isToday ? 'today' : ''
                    } ${day.isPast ? 'past' : ''} ${
                      day.isWeekend ? 'weekend' : ''
                    } ${selectedDate?.toDateString() === day.date.toDateString() ? 'selected' : ''} ${
                      day.isAvailable ? 'available' : ''
                    }`}
                    onClick={() => handleDateSelect(day)}
                    disabled={!day.isAvailable}
                  >
                    {day.date.getDate()}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Time Selection Section */}
          <motion.div 
            className="time-section"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {selectedDate ? (
              <>
                <h3>Beschikbare Tijden</h3>
                <p className="selected-date">
                  {selectedDate.toLocaleDateString('nl-NL', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
                
                <div className="time-slots">
                  {availableSlots.map((time) => (
                    <button
                      key={time}
                      className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                      onClick={() => handleTimeSelect(time)}
                    >
                      <Clock size={16} />
                      {time}
                    </button>
                  ))}
                </div>

                {selectedTime && (
                  <motion.div 
                    className="booking-summary"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h4>Afspraak Samenvatting</h4>
                    <div className="summary-details">
                      <div className="summary-item">
                        <Calendar size={16} />
                        <span>{selectedDate.toLocaleDateString('nl-NL')}</span>
                      </div>
                      <div className="summary-item">
                        <Clock size={16} />
                        <span>{selectedTime} - {selectedTime.split(':')[0]}:30</span>
                      </div>
                      <div className="summary-item">
                        <MapPin size={16} />
                        <span>Online of op locatie (wordt besproken)</span>
                      </div>
                    </div>
                    
                    <button 
                      className="btn btn-primary book-btn"
                      onClick={handleBooking}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Bezig met boeken...' : 'Bevestig Afspraak'}
                    </button>
                  </motion.div>
                )}
              </>
            ) : (
              <div className="no-date-selected">
                <Calendar size={48} />
                <p>Selecteer eerst een datum om beschikbare tijden te zien.</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
